/*eslint-disable id-length */
/*eslint max-depth: ["error", 5]*/
import { camelCase } from 'case-anything';
import { toast } from 'react-toastify';
import getT from 'next-translate/getT';
import type { FormikHelpers, FormikValues } from 'formik';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Checks if error is caused by API validation
 * @param error
 * @returns bool
 */
function isValidationError(error: unknown): error is FetchBaseQueryError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    error.status === 400 &&
    'data' in error &&
    //@ts-expect-error: TS does not understand the above checks appperently
    'errors' in error.data
  );
}

/**
 * Workaround for Formik's inability to set nested field errors.
 * Converts keys with dot-separated strings into nested keys
 * and camelCases them as .Net often returns them KebabCase
 * so we can validate 'some.nested.field'
 *
 * @param obj The input object with dot-separated keys
 * @returns The object with nested keys
 */
export function formatFormikErrors(obj: Record<string, any>): Record<string, any> {
  const cache = { ...obj };

  for (const key in cache) {
    const hasProperty = Object.prototype.hasOwnProperty.call(cache, key);
    if (hasProperty && key.includes('.')) {
      const keys = key.split('.');
      let data = cache;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = camelCase(keys[i], { keepSpecialCharacters: true });
        data = data[key] = data[key] || (keys[i + 1].match(/^\d+$/) ? [] : {});
      }
      const lastKey = camelCase(keys[keys.length - 1], { keepSpecialCharacters: true });

      data[lastKey] = cache[key];
      delete cache[key];
    }
  }

  return cache;
}

async function getStaticTranslation({ locale = 'nl-NL', key }: { locale?: string; key: string }): Promise<string> {
  const t = await getT(locale, ['common', 'form-errors', 'reset-password']);
  return t(key);
}

interface IFormikSubmit {
  actions: FormikHelpers<any>;
  queryFn: AppCallbackFn;
  values: FormikValues;
  showSuccess?: boolean;
  successMessage?: string;
}

/**
 * Helper function for Formik forms and RTK-Query mutations
 * By passing the RTK-Query mutationFn directly we don't have to manually update the query cache
 * The mutation result from the call site will update automatically
 *
 * @param {
 *  queryFn: RTKQ mutation function,
 *  values: formValues passed to the queryFn,
 *  actions: Formik Actions object
 * }
 * @returns the RTK-Query mutation result
 */
export async function formikSubmit({
  queryFn,
  values,
  actions,
  showSuccess = true,
  successMessage = 'common:toast.save-success'
}: IFormikSubmit) {
  try {
    const result = await queryFn({ ...values }).unwrap();
    if (showSuccess) {
      toast.success(`${await getStaticTranslation({ key: successMessage })}`);
    }
    // if result is empty/undefined/null, send back true to assure it was a success.
    return result || true;
  } catch (error) {
    if (isValidationError(error)) {
      // @ts-expect-error: data is `Unknown' however checks are inplace
      const formikErrors = formatFormikErrors(error?.data?.errors);
      actions.setErrors(formikErrors as any);
      toast.error(`${await getStaticTranslation({ key: 'common:form-fields-error-message' })}`);
    } else {
      throw error;
    }
  } finally {
    actions.setSubmitting(false);
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }
}
